import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Wrap,
} from "@chakra-ui/react";
import { INVOICES_STATES_VALUE_EMITTED } from "@raiden/library/constants/invoices";
import getErrorDetail from "@raiden/library/helpers/api/getErrorDetail";
import useAuthAs from "@raiden/library/hooks/auth/as";
import useRequest from "@raiden/library/hooks/useRequest";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useRouter } from "next/router";
import { useCallback, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

/**
 * @typedef {Object} Props
 * @property {number} invoiceId
 * @property {boolean} isOpen
 * @property {() => void} onClose
 *
 * @param {Props} props
 */
function WizardStepPaymentDialogPayOnSpot({
  invoiceId,
  isOpen,
  onClose: _onClose,
}) {
  const [isSuccess, setIsSuccess] = useState(false);

  /** @type {import("react").MutableRefObject<HTMLButtonElement | null>} */
  const ref = useRef(null);

  const { mutate } = useAuthAs();

  const { request, toastError } = useRequest();

  const { push } = useRouter();

  const onClose = useCallback(() => {
    _onClose();
    mutate();
  }, [_onClose, mutate]);

  const handleClickContinue = useCallback(() => {
    mutate();
    push(generateFrontPath({ id: "@account.payments.invoices" }));
  }, [mutate, push]);

  const payOnSpot = useCallback(() => {
    request(
      generateApiUri({ id: "@invoices.update", parameters: { invoiceId } }),
      {
        method: "POST",
        body: {
          data: {
            state: INVOICES_STATES_VALUE_EMITTED,
          },
        },
      },
    )
      .then(() => {
        setIsSuccess(true);
      })
      .catch((response) => {
        toastError({
          title: getErrorDetail({ error: response }),
        });
      });
  }, [invoiceId, request, toastError]);

  return (
    <Modal
      initialFocusRef={ref}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      isCentered>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          <FormattedMessage defaultMessage="Payer sur place" />
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Stack spacing="1rem">
            {!isSuccess && (
              <Text>
                <FormattedMessage defaultMessage="Êtes-vous sûr de vouloir payer sur place ?" />
              </Text>
            )}

            {isSuccess && (
              <>
                <Text>
                  <FormattedMessage defaultMessage="Rendez vous sur place au 1 Rue Louis Blériot, 88190 Golbey pour procéder au réglement." />
                </Text>

                <Text>
                  <FormattedMessage defaultMessage="Vos informations ont bien été sauvegardées mais resteront en attente tant que le paiement ne sera pas enregistré par nos services." />
                </Text>
              </>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Wrap>
            {!isSuccess && (
              <>
                <Button onClick={onClose} colorScheme="gray">
                  <FormattedMessage defaultMessage="Annuler" />
                </Button>

                <Button
                  ref={ref}
                  onClick={payOnSpot}
                  colorScheme="brandSecondary">
                  <FormattedMessage defaultMessage="Payer sur place" />
                </Button>
              </>
            )}

            {isSuccess && (
              <Button
                onClick={handleClickContinue}
                colorScheme="brandSecondary">
                <FormattedMessage defaultMessage="Continuer" />
              </Button>
            )}
          </Wrap>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default WizardStepPaymentDialogPayOnSpot;
